import {FC, useState, useEffect} from 'react'
import {SortableContainer, arrayMove} from 'react-sortable-hoc'

import {Table} from 'components/ui/Table'
import {Row, SortableRow} from './Row'

import styles from './List.scss'

import type {AlertType} from '../../query'
import type {Refetch} from '../../index'

interface ListProps {
  alertTypes: AlertType[]
  isEditing: boolean
  setOrdinals: (ids: number[], ordinals: number[]) => void
  refetch: Refetch
  page: number
  limit: number
}

interface SortableAlertTypeTableProps {
  sortedAlertTypes: AlertType[]
  refetch: Refetch
  showMissingOfficeColumn: boolean
}

interface AlertTypeTableProps {
  alertTypes: AlertType[]
  refetch: Refetch
  firstIndex: number
  showMissingOfficeColumn: boolean
}

interface OnSortEndParams {
  oldIndex: number
  newIndex: number
}

const SortableAlertTypeTable = SortableContainer<SortableAlertTypeTableProps>(
  ({sortedAlertTypes, showMissingOfficeColumn}: SortableAlertTypeTableProps) => {
    return (
      <Table.Tbody>
        {sortedAlertTypes.map((alertType, index) => (
          <SortableRow
            key={alertType.id}
            index={index}
            alert={alertType}
            showMissingOfficeColumn={showMissingOfficeColumn}
          />
        ))}
      </Table.Tbody>
    )
  }
)

const AlertTypeTable: FC<AlertTypeTableProps> = ({alertTypes, refetch, firstIndex, showMissingOfficeColumn}) => {
  return (
    <Table.Tbody>
      {alertTypes.map((alertType, index) => (
        <Row
          key={alertType.id}
          index={firstIndex + index}
          alert={alertType}
          refetch={refetch}
          showMissingOfficeColumn={showMissingOfficeColumn}
        />
      ))}
    </Table.Tbody>
  )
}

export const List: FC<ListProps> = ({alertTypes, isEditing, setOrdinals, refetch, page, limit}) => {
  const [sortedAlertTypes, setSortedAlertTypes] = useState<AlertType[]>([])

  useEffect(() => {
    if (alertTypes && !isEditing) {
      setSortedAlertTypes(alertTypes)
    }
  }, [alertTypes, isEditing])

  useEffect(() => {
    setOrdinals(
      sortedAlertTypes?.map((alertType: AlertType) => alertType.id),
      alertTypes?.map((alertType: AlertType) => alertType.alertTypeOrdinal.ordinal)
    )
  }, [sortedAlertTypes])

  const onSortEnd = ({oldIndex, newIndex}: OnSortEndParams) => {
    setSortedAlertTypes((prevAlertTypes) => arrayMove(prevAlertTypes, oldIndex, newIndex))
  }
  const firstIndex = limit * (page - 1) + 1

  const showMissingOfficeColumn = alertTypes.some((alert) => alert.officesEmpty)

  return (
    <>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th className={styles.numberColumn}>No.</Table.Th>
            <Table.Th className={styles.colorColumn}>カラー</Table.Th>
            <Table.Th className={styles.nameColumn}>アラート名</Table.Th>
            <Table.Th className={styles.explanationColumn}>説明</Table.Th>
            <Table.Th className={styles.baseDateColumn}>通知基準</Table.Th>
            <Table.Th className={styles.alertTargetColumn}>対象者</Table.Th>
            <Table.Th className={styles.adminMailColumn}>メール(管理者)</Table.Th>
            <Table.Th className={styles.employeeMailColumn}>メール(従業員)</Table.Th>
            <Table.Th className={styles.editColumn}></Table.Th>
            <Table.Th className={styles.deleteColumn}></Table.Th>
            {showMissingOfficeColumn && <Table.Th className={styles.missingOfficeColumn}></Table.Th>}
          </Table.Tr>
        </Table.Thead>

        {isEditing ? (
          <SortableAlertTypeTable
            lockAxis="y"
            onSortEnd={onSortEnd}
            helperClass={styles.dragging}
            sortedAlertTypes={sortedAlertTypes}
            refetch={refetch}
            showMissingOfficeColumn={showMissingOfficeColumn}
          />
        ) : (
          <AlertTypeTable
            alertTypes={alertTypes}
            refetch={refetch}
            firstIndex={firstIndex}
            showMissingOfficeColumn={showMissingOfficeColumn}
          />
        )}
      </Table>
    </>
  )
}
